import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, X, Users, Flame } from "lucide-react";
import api from "../lib/api";

const DISMISS_KEY = "bdu_supporter_ticker_dismissed_v1";
const POLL_INTERVAL_MS = 45_000;

function timeAgo(iso) {
  if (!iso) return "just now";
  const t = new Date(iso).getTime();
  if (!t || Number.isNaN(t)) return "just now";
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const wks = Math.floor(days / 7);
  return `${wks}w ago`;
}

function fmtDollar(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `$${(v / 1_000).toFixed(0)}K`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

/**
 * Scrolling top-of-page ticker. Shows:
 *   - Live counter (N of 3,000 charter supporters)
 *   - Recent pledges: "{First} from {City} just pledged · {amount}"
 *   - 7-day momentum: "+N supporters this week · ${X} pledged · {Y} in-FOM"
 *
 * Marquee: content is duplicated twice side-by-side and the container slides
 * translateX(-50%) over 50s so the loop is seamless. CSS animation pauses on
 * hover and when the tab is hidden (via a visibility-change listener).
 *
 * Dismissible — remembered in localStorage.
 */
export default function SupporterTicker() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [paused, setPaused] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch (_e) { /* ignore */ }
  }, []);

  const load = async () => {
    try {
      const r = await api.get("/credit-union/ticker", { params: { limit: 15 } });
      setData(r.data);
    } catch (_e) {
      /* silent — ticker just stays stale */
    }
  };

  useEffect(() => {
    if (dismissed) return;
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dismissed]);

  const items = useMemo(() => buildItems(data), [data]);

  if (dismissed) return null;

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch (_e) {}
    setDismissed(true);
  };

  const count = data?.total_count || 0;
  const goal = data?.goal || 3000;

  return (
    <div
      className="relative overflow-hidden border-b border-bdu-gold/25"
      data-testid="supporter-ticker"
      style={{
        background:
          "linear-gradient(90deg, rgba(10,10,10,0.95) 0%, rgba(18,16,14,0.98) 50%, rgba(10,10,10,0.95) 100%)",
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Ticker css (keyframes + helpers) */}
      <style>{`
        @keyframes bdu-ticker-slide {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .bdu-ticker-track {
          display: inline-flex;
          align-items: center;
          white-space: nowrap;
          animation: bdu-ticker-slide 55s linear infinite;
          will-change: transform;
        }
        .bdu-ticker-track[data-paused="true"] { animation-play-state: paused; }
        @media (max-width: 640px) {
          .bdu-ticker-track { animation-duration: 42s; }
        }
        @media (prefers-reduced-motion: reduce) {
          .bdu-ticker-track { animation: none; }
        }
      `}</style>

      <div className="relative flex items-stretch">
        {/* Left label — stays fixed */}
        <div
          className="hidden sm:flex items-center gap-2 px-4 py-2 flex-shrink-0 border-r border-bdu-gold/20 bg-bdu-gold text-black font-bold text-[10px] uppercase tracking-[0.26em] z-10"
          data-testid="supporter-ticker-label"
        >
          <Flame size={12} />
          <span>Live Roll</span>
          <span className="opacity-70">·</span>
          <span data-testid="supporter-ticker-count">
            {count}/{goal}
          </span>
        </div>

        {/* Marquee */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-hidden py-2 relative"
          data-testid="supporter-ticker-marquee"
        >
          <div
            className="bdu-ticker-track"
            data-paused={paused ? "true" : "false"}
            data-testid="supporter-ticker-track"
          >
            {/* Content copy 1 */}
            <TickerContent items={items} />
            {/* Content copy 2 — identical for seamless loop */}
            <TickerContent items={items} ariaHidden />
          </div>

          {/* Edge fades */}
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-10"
            style={{ background: "linear-gradient(90deg, rgba(10,10,10,1), rgba(10,10,10,0))" }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-10"
            style={{ background: "linear-gradient(90deg, rgba(10,10,10,0), rgba(10,10,10,1))" }}
          />
        </div>

        {/* Right CTA — stays fixed */}
        <Link
          to="/credit-union"
          className="hidden md:flex items-center gap-2 px-4 py-2 flex-shrink-0 border-l border-bdu-gold/20 text-[11px] uppercase tracking-[0.24em] text-bdu-cream hover:text-bdu-gold transition-colors z-10"
          data-testid="supporter-ticker-cta"
        >
          Join The Roll →
        </Link>

        {/* Dismiss */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss ticker"
          className="flex items-center justify-center px-2 flex-shrink-0 border-l border-bdu-gold/20 text-bdu-cream/50 hover:text-bdu-gold transition-colors z-10"
          data-testid="supporter-ticker-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function TickerContent({ items, ariaHidden }) {
  return (
    <div
      className="flex items-center text-xs text-bdu-cream/80"
      aria-hidden={ariaHidden ? "true" : undefined}
    >
      {items.map((it, i) => (
        <TickerItem key={`${it.key}-${i}`} item={it} />
      ))}
    </div>
  );
}

function TickerItem({ item }) {
  if (item.type === "pledge") {
    return (
      <span className="inline-flex items-center gap-2 px-6 flex-shrink-0">
        <span
          className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"
          style={{ boxShadow: "0 0 10px rgba(52,211,153,0.8)" }}
          aria-hidden
        />
        <span className="font-serif text-sm text-bdu-cream">{item.first_name}</span>
        <span className="text-bdu-cream/55">from {item.city}</span>
        <span className="text-bdu-gold/70">·</span>
        <span className="uppercase tracking-widest text-[10px] text-emerald-400 font-semibold">
          just pledged
        </span>
        {item.amount ? (
          <span className="text-bdu-gold font-semibold tracking-wider">
            {fmtDollar(item.amount)}
          </span>
        ) : null}
        <span className="text-bdu-cream/40 text-[10px] italic">{item.when}</span>
      </span>
    );
  }
  if (item.type === "momentum") {
    return (
      <span className="inline-flex items-center gap-2 px-6 flex-shrink-0 border-l border-bdu-gold/15">
        <TrendingUp size={13} className="text-bdu-gold" />
        <span className="uppercase tracking-widest text-[10px] text-bdu-gold font-semibold">
          This Week
        </span>
        <span className="text-bdu-cream">
          +<span className="font-semibold">{item.weekCount}</span> supporters
        </span>
        <span className="text-bdu-gold/70">·</span>
        <span className="text-bdu-cream">
          <span className="font-semibold">{fmtDollar(item.weekPledged)}</span> pledged
        </span>
        {item.weekInFom ? (
          <>
            <span className="text-bdu-gold/70">·</span>
            <span className="text-bdu-cream">
              <span className="font-semibold">{item.weekInFom}</span> in FOM
            </span>
          </>
        ) : null}
      </span>
    );
  }
  if (item.type === "goal") {
    return (
      <span className="inline-flex items-center gap-2 px-6 flex-shrink-0 border-l border-bdu-gold/15">
        <Users size={13} className="text-bdu-gold" />
        <span className="uppercase tracking-widest text-[10px] text-bdu-gold font-semibold">
          Charter Goal
        </span>
        <span className="text-bdu-cream">
          <span className="font-semibold">{item.remaining.toLocaleString()}</span> seats remaining of
        </span>
        <span className="text-bdu-cream font-semibold">{item.goal.toLocaleString()}</span>
      </span>
    );
  }
  if (item.type === "empty") {
    return (
      <span className="inline-flex items-center gap-2 px-6 flex-shrink-0">
        <Flame size={13} className="text-bdu-gold" />
        <span className="text-bdu-cream">
          Be <span className="font-serif italic text-bdu-gold">supporter #1</span> on the charter roll
        </span>
        <span className="text-bdu-gold/70">·</span>
        <span className="uppercase tracking-widest text-[10px] text-bdu-cream/60">
          Pledge $100 · held in escrow · refundable
        </span>
      </span>
    );
  }
  return null;
}

function buildItems(data) {
  if (!data) {
    return [
      { key: "empty-1", type: "empty" },
      { key: "goal-1", type: "goal", remaining: 3000, goal: 3000 },
    ];
  }
  const items = [];
  const recent = data.recent || [];
  if (recent.length === 0) {
    items.push({ key: "empty", type: "empty" });
  } else {
    recent.forEach((r, i) => {
      items.push({
        key: `p-${i}`,
        type: "pledge",
        first_name: r.first_name || "Supporter",
        city: r.city || "Atlanta",
        amount: r.pledge_amount || 0,
        when: timeAgo(r.signed_at),
      });
    });
  }
  const week = data.week || {};
  if ((week.count || 0) > 0) {
    items.push({
      key: "mom",
      type: "momentum",
      weekCount: week.count || 0,
      weekPledged: week.pledged || 0,
      weekInFom: week.in_fom || 0,
    });
  }
  items.push({
    key: "goal",
    type: "goal",
    remaining: data.seats_remaining || 0,
    goal: data.goal || 3000,
  });
  return items;
}
